/**
 * Dossiers: the support case queue (WALLIYO-0035). The contract for the
 * console's case screens and for the app's "contact support".
 *
 * - **One shared queue.** Agents claim cases; a manager with REASSIGN_CASES
 *   can hand one to someone else. A case has at most one owner.
 * - **One timeline per case**, whatever the channel: calls the agent logs,
 *   in-app chat, email, and internal notes. Staff reply in the app, and the
 *   customer gets an in-app notification (WALLIYO-0098: notifications, not
 *   SMS). SMS replies from before that stay on the timeline.
 * - **A reply deadline.** Every case owes the customer a first reply by
 *   `respondBy`; a case past it with no reply is `late`.
 * - **No WhatsApp, ever.** Someone who can't use the app is called back.
 *
 * Every write names the `version` the screen was showing (ADR-0016 §2.3) and
 * takes an `Idempotency-Key` header. Every endpoint answers under its own
 * response name (WALLIYO-0068).
 */

// ── Vocabulary ───────────────────────────────────────────────────────────

/** How a case was opened. Later contacts may come by any channel. */
/**
 * What kind of work a case is (WALLIYO-0131).
 *
 * `customer` came in from outside. `access` is somebody here asking for a
 * permission they do not hold: it goes to whoever can grant it and waits in
 * their queue, with the same claiming, timeline and audit as any other case.
 */
export type SupportCaseKind = "customer" | "access";

/** How a customer case was opened. Null on an access case: nobody phoned in. */
export type SupportCaseChannel = "phone" | "email" | "chat";

export type SupportCaseStatus = "open" | "waiting_on_customer" | "closed";

export type SupportCasePriority = "low" | "normal" | "high" | "urgent";

/** One entry on the timeline. */
export type SupportCaseEntryType = "call" | "email" | "chat" | "note" | "sms";

/** `internal` never reaches the customer: notes, and an agent's call summary. */
export type SupportCaseEntryVisibility = "customer" | "internal";

/** Whether an old SMS reply reached the provider. Replies are no longer sent by SMS. */
export type SupportCaseSmsStatus = "pending" | "sent" | "failed";

export const SUPPORT_CASE_STATUSES: readonly SupportCaseStatus[] = ["open", "waiting_on_customer", "closed"];
export const SUPPORT_CASE_CHANNELS: readonly SupportCaseChannel[] = ["phone", "email", "chat"];
export const SUPPORT_CASE_PRIORITIES: readonly SupportCasePriority[] = ["low", "normal", "high", "urgent"];

/**
 * How long a customer waits, at most, for a first reply.
 *
 * TODO(WALLIYO-0035): read FIRST_REPLY_TARGET_RULE once the rules module is
 * merged; this constant is its default until then.
 */
export const FIRST_REPLY_TARGET_MINUTES = 120;

export const SUPPORT_CASES_DEFAULT_LIMIT = 50;
export const SUPPORT_CASES_MAX_LIMIT = 200;

// ── The queue ────────────────────────────────────────────────────────────

export type SupportCaseCustomer = {
  userId: string;
  name: string;
  /** e.g. "+224 6•• •• •• 41". Revealing the number is its own action. */
  maskedPhone: string | null;
};

export type SupportCaseOwner = {
  staffMemberId: string;
  name: string;
};

/** The ask on an access case: what, for whom, until when. */
export type AccessRequest = {
  /** The permission they do not hold. */
  settingId: string;
  /** Who wants it. Granted to them alone, never to their team. */
  staffMemberId: string;
  uuid: number;
  name: string;
  /** What they are on, for whoever decides. A team grants nothing; it is context. */
  teams: readonly string[];
  roles: readonly string[];
  /** When it would end by itself. Null means they did not say. */
  until: string | null;
};

export type SupportCaseListItem = {
  supportCaseId: string;
  /** What people say on the phone, e.g. "D-0142". */
  reference: string;
  subject: string;
  kind: SupportCaseKind;
  /** Null on an access case. */
  channel: SupportCaseChannel | null;
  /** What is being asked for, on an access case; null on a customer one. */
  request: AccessRequest | null;
  status: SupportCaseStatus;
  priority: SupportCasePriority;
  /** Null for an unknown caller, until the case is linked to a customer. */
  customer: SupportCaseCustomer | null;
  /** Null while it waits in the queue. */
  owner: SupportCaseOwner | null;
  /** When the customer must have had a first reply. */
  respondBy: string;
  firstRepliedAt: string | null;
  /** Not closed, no first reply yet, and past `respondBy`. */
  late: boolean;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
};

/**
 * GET /api/v1/console/cases. Needs CLAIM_CASES or REASSIGN_CASES.
 *
 * The queue: unassigned cases first, then the earliest `respondBy`. With no
 * `status`, closed cases are left out.
 */
export type ListSupportCasesRequest = {
  /** `mine`: owned by the caller. `unassigned`: waiting to be claimed. */
  assigned?: "mine" | "unassigned";
  late?: boolean;
  status?: SupportCaseStatus;
  channel?: SupportCaseChannel;
  /** One customer's cases. */
  userId?: string;
  cursor?: string;
  limit?: number;
};

export type ListSupportCasesResponse = {
  cases: SupportCaseListItem[];
  /** Pass back as `cursor` for the next page; null on the last page. */
  nextCursor: string | null;
};

// ── One case and its timeline ─────────────────────────────────────────────

export type SupportCaseEntryAuthor =
  | { type: "staff"; staffMemberId: string; name: string }
  | { type: "customer"; userId: string; name: string };

export type SupportCaseEntry = {
  supportCaseEntryId: string;
  type: SupportCaseEntryType;
  visibility: SupportCaseEntryVisibility;
  author: SupportCaseEntryAuthor;
  body: string;
  /** Calls only. */
  callDurationSeconds: number | null;
  /** Old SMS replies only: where it went and whether it arrived at the provider. */
  sms: { maskedTo: string; status: SupportCaseSmsStatus; statusAt: string | null } | null;
  createdAt: string;
};

export type SupportCaseDetail = SupportCaseListItem & {
  /** The number an unknown caller gave, masked. */
  maskedContactPhone: string | null;
  closeReason: string | null;
  /** Oldest first. Staff see internal entries; customers never do. */
  entries: SupportCaseEntry[];
};

/** GET /api/v1/console/cases/:supportCaseId */
export type GetSupportCaseResponse = { supportCase: SupportCaseDetail };

// ── Writes ────────────────────────────────────────────────────────────────
//
// Each answers with the case as it is now. `reason` is optional where the case
// itself is the reason (claiming, notes, replies, calls) and required where a
// decision is being made (reassigning, closing, linking a customer).

/**
 * POST /api/v1/console/cases: log a phone call and open a case for it
 * (LOG_CALL). The agent who logs it owns it, and the call is its first reply.
 */
export type OpenSupportCaseRequest = {
  subject: string;
  /** What was said: an internal summary of the call. */
  body: string;
  callDurationSeconds?: number | null;
  /** The customer, when known. */
  userId?: string | null;
  /** The number an unknown caller gave, in E.164. */
  contactPhone?: string | null;
  priority?: SupportCasePriority;
  reason?: string;
};
export type OpenSupportCaseResponse = { supportCase: SupportCaseDetail };

/** POST /api/v1/console/cases/:supportCaseId/calls: another call on the same case (LOG_CALL). */
export type LogCaseCallRequest = {
  body: string;
  callDurationSeconds?: number | null;
  version: number;
  reason?: string;
};
export type LogCaseCallResponse = { supportCase: SupportCaseDetail };

/** POST /api/v1/console/cases/:supportCaseId/claim (CLAIM_CASE). Only one agent can win. */
export type ClaimSupportCaseRequest = { version: number; reason?: string };
export type ClaimSupportCaseResponse = { supportCase: SupportCaseDetail };

/** POST /api/v1/console/cases/:supportCaseId/reassign (REASSIGN_CASE, needs REASSIGN_CASES). */
export type ReassignSupportCaseRequest = { toStaffMemberId: string; version: number; reason: string };
export type ReassignSupportCaseResponse = { supportCase: SupportCaseDetail };

/** POST /api/v1/console/cases/:supportCaseId/notes (ADD_CASE_NOTE). Never shown to the customer. */
export type AddCaseNoteRequest = { body: string; version: number; reason?: string };
export type AddCaseNoteResponse = { supportCase: SupportCaseDetail };

/**
 * POST /api/v1/console/cases/:supportCaseId/replies (REPLY_TO_CASE). Only the
 * case's owner replies, in the app: the customer gets a `support_case_reply`
 * notification, saved with the reply. Never by SMS. A case with no customer
 * account (an unknown caller) is refused with `no_customer`: link the
 * customer first, or call them back.
 */
export type ReplyToCaseRequest = {
  body: string;
  version: number;
  reason?: string;
};
export type ReplyToCaseResponse = { supportCase: SupportCaseDetail };

/** POST /api/v1/console/cases/:supportCaseId/close (CLOSE_CASE). The owner, or a manager. */
export type CloseSupportCaseRequest = { reason: string; version: number };
export type CloseSupportCaseResponse = { supportCase: SupportCaseDetail };

/** POST /api/v1/console/cases/:supportCaseId/customer (LINK_CASE_CUSTOMER): who this caller is. */
export type LinkCaseCustomerRequest = { userId: string; version: number; reason: string };
export type LinkCaseCustomerResponse = { supportCase: SupportCaseDetail };

// ── The customer's side, in the app ───────────────────────────────────────

/** A message the customer can see: never an internal note or call summary. */
export type CustomerSupportMessage = {
  supportCaseEntryId: string;
  from: "customer" | "support";
  channel: "chat" | "sms" | "email";
  body: string;
  createdAt: string;
};

export type CustomerSupportCase = {
  supportCaseId: string;
  reference: string;
  subject: string;
  status: SupportCaseStatus;
  createdAt: string;
  messages: CustomerSupportMessage[];
};

/**
 * POST /api/v1/support/contact (CONTACT_SUPPORT, gated by CONTACT_US). Opens a
 * chat case in the queue, or, with `supportCaseId`, adds the message to one of
 * the caller's own cases that isn't closed (e.g. the one about their
 * suspension). Takes an `Idempotency-Key` header.
 */
export type ContactSupportRequest = { subject?: string; message: string; supportCaseId?: string | null };
export type ContactSupportResponse = { supportCase: CustomerSupportCase };

/** GET /api/v1/support/cases: the customer's own cases, newest first. */
export type ListMySupportCasesResponse = { cases: CustomerSupportCase[] };

// ── Your own requests (WALLIYO-0131) ─────────────────────────────────────

/**
 * Where a request you made has got to.
 *
 * `waiting` while nobody has answered; `granted` or `refused` once somebody
 * has. Deliberately not "do you hold it now" — a grant can be revoked or
 * expire afterwards, and "it was refused" and "it was granted and has since
 * gone" are different answers to the person who asked.
 */
export type MyAccessRequestState = "waiting" | "granted" | "refused";

export type MyAccessRequest = {
  supportCaseId: string;
  reference: string;
  settingId: string;
  state: MyAccessRequestState;
  /** What they asked it should end on. Null: open-ended. */
  until: string | null;
  askedAt: string;
  /** When somebody must answer, while it waits. */
  decideBy: string;
  /** Past `decideBy` and still waiting: somebody is blocked. */
  overdue: boolean;
  answeredAt: string | null;
  /** Why they said yes or no. The refusal is the part worth reading. */
  answer: string | null;
  /** Who has it, or who answered it. Null while nobody has claimed it. */
  withWhom: string | null;
};

/**
 * GET /api/v1/console/access-requests/mine
 *
 * Your own, newest first. Needs no feature: it is your own access, and asking
 * needed none either.
 */
export type ListMyAccessRequestsResponse = {
  requests: readonly MyAccessRequest[];
};
